/**
 * Settings Page - User settings and preferences
 * Based on mobile app SettingsScreen.tsx
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle2,
  BarChart3,
  UserCircle,
  Lock,
  Eye,
  Bell,
  BellOff,
  MessageSquare,
  UserPlus,
  AtSign,
  HelpCircle,
  Mail,
  FileText,
  Shield,
  Download,
  Trash2,
  LogOut,
  Camera,
  FileEdit,
  AlertTriangle,
} from 'lucide-react'
import SettingRow from '../components/settings/SettingRow'
import api from '../services/api'

export default function Settings() {
  const navigate = useNavigate()

  // Privacy Settings
  const [privateAccount, setPrivateAccount] = useState(false)
  const [reporterMode, setReporterMode] = useState(false)
  const [autoPublish, setAutoPublish] = useState(false)
  const [contentModeration, setContentModeration] = useState(true)

  // Notification Settings
  const [likesNotifications, setLikesNotifications] = useState(true)
  const [commentsNotifications, setCommentsNotifications] = useState(true)
  const [followersNotifications, setFollowersNotifications] = useState(true)
  const [mentionsNotifications, setMentionsNotifications] = useState(true)

  const handleAutoPublishToggle = (value: boolean) => {
    if (value) {
      if (window.confirm(
        'Auto-Publish will automatically make your posts public after uploading. ' +
        'Are you sure you want to enable this?'
      )) {
        setAutoPublish(true)
      }
    } else {
      setAutoPublish(false)
    }
  }

  const handleExportData = async () => {
    if (window.confirm(
      'We will collect your data and send you an email when it\'s ready for download. ' +
      'This may take a few minutes. Continue?'
    )) {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('https://auth.onrecord.live/auth/export-data', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
        if (response.ok) {
          alert('Your data export request has been submitted. You will receive an email when it\'s ready.')
        } else {
          alert('Data export is being prepared. Please check your email.')
        }
      } catch (error) {
        console.error('Export data error:', error)
        alert('Data export request submitted. You will receive an email when ready.')
      }
    }
  }

  const handleDeleteAccount = async () => {
    const action = window.prompt(
      'Account Deletion\n\n' +
      '1. Deactivate (temporary, can be reversed)\n' +
      '2. Delete (permanent, 30-day cooling period)\n\n' +
      'Enter 1 or 2:'
    )

    const token = localStorage.getItem('token')

    if (action === '1') {
      if (window.confirm('Are you sure you want to deactivate your account? You can reactivate it later.')) {
        try {
          await fetch('https://auth.onrecord.live/auth/deactivate', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          })
        } catch {}
        localStorage.setItem('account_deactivated', 'true')
        alert('Your account has been deactivated. You can reactivate it by logging in again.')
        handleLogout()
      }
    } else if (action === '2') {
      if (window.confirm(
        'Are you sure you want to delete your account?\n\n' +
        'Your account will be scheduled for deletion in 30 days. ' +
        'You can cancel this at any time during the cooling period.'
      )) {
        try {
          await fetch('https://auth.onrecord.live/auth/delete-account', {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          })
        } catch {}

        const deletionDate = new Date()
        deletionDate.setDate(deletionDate.getDate() + 30)
        localStorage.setItem('account_deletion_status', JSON.stringify({
          scheduledDate: deletionDate.toISOString(),
          daysRemaining: 30,
        }))

        alert('Your account has been scheduled for deletion in 30 days.')
        navigate('/profile')
      }
    }
  }

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to log out?')) {
      try {
        await api.logout()
        navigate('/login')
      } catch (error) {
        console.error('Logout error:', error)
        // Still navigate to login even if API call fails
        navigate('/login')
      }
    }
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white bg-opacity-90 backdrop-blur-sm border-b border-gray-200 z-10">
        <div className="flex items-center gap-4 px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-700 hover:text-onrecord-orange rounded-full p-1 hover:bg-orange-50"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold">Settings</h1>
        </div>
      </div>

      {/* Settings Content */}
      <div className="max-w-2xl mx-auto">
        {/* Account Section */}
        <div className="mt-6">
          <h2 className="px-4 py-2 text-small font-semibold text-text-secondary uppercase tracking-wide">
            Account
          </h2>
          <div className="bg-white border-y border-gray-200 divide-y divide-gray-200">
            <SettingRow
              icon={<CheckCircle2 className="w-5 h-5" />}
              title="Get Verified"
              subtitle="Apply for account verification"
              onClick={() => navigate('/verification')}
            />
            <SettingRow
              icon={<BarChart3 className="w-5 h-5" />}
              title="Analytics"
              subtitle="View your account analytics"
              onClick={() => navigate('/analytics')}
            />
            <SettingRow
              icon={<UserCircle className="w-5 h-5" />}
              title="Edit Profile"
              subtitle="Update your profile information"
              onClick={() => navigate('/edit-profile')}
            />
            <SettingRow
              icon={<FileEdit className="w-5 h-5" />}
              title="Drafts"
              subtitle="Manage your saved drafts"
              onClick={() => navigate('/drafts')}
            />
          </div>
        </div>

        {/* Privacy Section */}
        <div className="mt-6">
          <h2 className="px-4 py-2 text-small font-semibold text-text-secondary uppercase tracking-wide">
            Privacy
          </h2>
          <div className="bg-white border-y border-gray-200 divide-y divide-gray-200">
            <SettingRow
              icon={<Lock className="w-5 h-5" />}
              title="Private Account"
              subtitle="Only approved followers can see your posts"
              type="toggle"
              value={privateAccount}
              onChange={setPrivateAccount}
            />
            <SettingRow
              icon={<Eye className="w-5 h-5" />}
              title="Reporter Mode"
              subtitle="Enhanced features for journalists"
              type="toggle"
              value={reporterMode}
              onChange={setReporterMode}
            />
            <SettingRow
              icon={<Camera className="w-5 h-5" />}
              title="Auto-Publish"
              subtitle="Automatically publish web uploads"
              type="toggle"
              value={autoPublish}
              onChange={handleAutoPublishToggle}
            />
            <SettingRow
              icon={<AlertTriangle className="w-5 h-5" />}
              title="Content Moderation"
              subtitle="Enable automatic content safety checks"
              type="toggle"
              value={contentModeration}
              onChange={setContentModeration}
            />
          </div>
        </div>

        {/* Notifications Section */}
        <div className="mt-6">
          <h2 className="px-4 py-2 text-small font-semibold text-text-secondary uppercase tracking-wide">
            Notifications
          </h2>
          <div className="bg-white border-y border-gray-200 divide-y divide-gray-200">
            <SettingRow
              icon={likesNotifications ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
              title="Likes"
              subtitle="Get notified when someone likes your posts"
              type="toggle"
              value={likesNotifications}
              onChange={setLikesNotifications}
            />
            <SettingRow
              icon={commentsNotifications ? <MessageSquare className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
              title="Comments"
              subtitle="Get notified when someone comments on your posts"
              type="toggle"
              value={commentsNotifications}
              onChange={setCommentsNotifications}
            />
            <SettingRow
              icon={followersNotifications ? <UserPlus className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
              title="New Followers"
              subtitle="Get notified when someone follows you"
              type="toggle"
              value={followersNotifications}
              onChange={setFollowersNotifications}
            />
            <SettingRow
              icon={mentionsNotifications ? <AtSign className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
              title="Mentions"
              subtitle="Get notified when someone mentions you"
              type="toggle"
              value={mentionsNotifications}
              onChange={setMentionsNotifications}
            />
          </div>
        </div>

        {/* Support Section */}
        <div className="mt-6">
          <h2 className="px-4 py-2 text-small font-semibold text-text-secondary uppercase tracking-wide">
            Support
          </h2>
          <div className="bg-white border-y border-gray-200 divide-y divide-gray-200">
            <SettingRow
              icon={<HelpCircle className="w-5 h-5" />}
              title="Help Center"
              subtitle="Get help with OnRecord"
              onClick={() => window.open('https://help.onrecord.com', '_blank')}
            />
            <SettingRow
              icon={<Mail className="w-5 h-5" />}
              title="Contact Support"
              subtitle="Get in touch with our team"
              onClick={() => window.open('mailto:contact@onrecord.live')}
            />
            <SettingRow
              icon={<Shield className="w-5 h-5" />}
              title="Privacy Policy"
              subtitle="Read our privacy policy"
              onClick={() => window.open('/privacy', '_blank')}
            />
            <SettingRow
              icon={<FileText className="w-5 h-5" />}
              title="Terms of Service"
              subtitle="Read our terms of service"
              onClick={() => window.open('/terms', '_blank')}
            />
          </div>
        </div>

        {/* Danger Zone */}
        <div className="mt-6 mb-8">
          <h2 className="px-4 py-2 text-small font-semibold text-error uppercase tracking-wide">
            Danger Zone
          </h2>
          <div className="bg-white border-y border-gray-200 divide-y divide-gray-200">
            <SettingRow
              icon={<Download className="w-5 h-5" />}
              title="Export My Data"
              subtitle="Download a copy of your data"
              onClick={handleExportData}
            />
            <SettingRow
              icon={<Trash2 className="w-5 h-5" />}
              title="Delete Account"
              subtitle="Permanently delete your account"
              onClick={handleDeleteAccount}
              danger
            />
            <SettingRow
              icon={<LogOut className="w-5 h-5" />}
              title="Logout"
              subtitle="Sign out of your account"
              onClick={handleLogout}
              danger
            />
          </div>
        </div>

        {/* App Version */}
        <div className="px-4 py-8 text-center text-small text-text-secondary">
          on record Web v1.0.0
        </div>
      </div>
    </div>
  )
}
